import React, { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { useRouter } from "expo-router";
import analytics from "@react-native-firebase/analytics";

import UsernameInput from "./Components/UsernameInput";

import { ApiError } from "@/schemas/api/error";
import { isVerificationCodeValid } from "@/schemas/forms/code";
import { isValidUsername } from "@/schemas/forms/username";
import AuthService from "@/services/internal/auth";
import { alertUnexpectedApiError, isApiError } from "@/utils/alerts";
import { getLoginCredentialsFromStorage } from "@/hooks/authentication";
import { getDeviceIdentifier, logDeviceData } from "@/hooks/device";
import { useLocationPermission } from "@/hooks/location";
import { useUpdateInfo } from "@/screens/Settings/Troubleshooting/Services/utils/fetchUpdateInfo";

function VerifyAccountForm(): React.JSX.Element {
  const router = useRouter();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [username, setUsername] = useState<string>("");
  const [verificationCode, setVerificationCode] = useState<string>("");
  const { location } = useLocationPermission();
  const updateInfo = useUpdateInfo();

  const fetchCreds = async (): Promise<void> => {
    const creds = await getLoginCredentialsFromStorage();
    if (creds) {
      setUsername(creds.username);
    }
  };

  useEffect(() => {
    fetchCreds();
  }, []);

  const validateInputs = (): boolean => {
    const codeValidation = isVerificationCodeValid(verificationCode);
    if (!codeValidation.isValid) {
      Alert.alert("Invalid Verification Code", codeValidation.message, [
        { text: "OK" },
      ]);
      return false;
    }
    const usernameValidation = isValidUsername(username);
    if (!usernameValidation.isValid) {
      Alert.alert("Invalid Username", usernameValidation.message, [
        { text: "OK" },
      ]);
      return false;
    }
    return true;
  };

  const handleSubmit = async (): Promise<void> => {
    setIsLoading(true);

    if (!validateInputs()) {
      setIsLoading(false);
      return;
    }

    const { deviceId } = await getDeviceIdentifier();
    const authService = new AuthService();

    try {
      await authService.verifyAccount({
        username: username,
        code: verificationCode,
        deviceId,
      });
      await analytics().logEvent("app_sign_up_success");
      await logDeviceData({
        location,
        updates: updateInfo,
        forceLogging: true,
      });
      Alert.alert(
        "Account Verified",
        "Your account has been verified successfully. Please login.",
        [
          {
            text: "OK",
            onPress: () =>
              router.replace({
                pathname: "/(authentication)/sign-in",
              }),
          },
        ],
      );
    } catch (error) {
      if (isApiError(error)) {
        const apiError = error;
        alertUnexpectedApiError(apiError);
      } else {
        const errorMessage =
          error instanceof Error ? error.message : "An unknown error occurred";
        Alert.alert("Error", errorMessage, [{ text: "OK" }]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async (): Promise<void> => {
    setIsLoading(true);

    const usernameValidation = isValidUsername(username);
    if (!usernameValidation.isValid) {
      Alert.alert("Invalid Username", usernameValidation.message, [
        { text: "OK" },
      ]);
      setIsLoading(false);
      return;
    }

    try {
      const authService = new AuthService();
      await authService.resendVerificationCode(username);
      Alert.alert(
        "Verification Code Sent",
        `A new verification code has been sent to email address associated with ${username}`,
        [{ text: "OK" }],
      );
    } catch (error) {
      const apiError = error as ApiError;
      alertUnexpectedApiError(apiError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.formContainer}
      >
        <View style={styles.card}>
          <Text style={styles.title}>Verify Account</Text>
          <Text style={styles.label}>Username</Text>
          <UsernameInput
            value={username}
            onChangeText={setUsername}
            placeholder="Provide your username"
          />
          <Text style={styles.label}>Verification Code</Text>
          <TextInput
            testID="verification-code-input"
            style={styles.input}
            value={verificationCode}
            onChangeText={(text) =>
              setVerificationCode(text.replace(/[^0-9]/g, ""))
            }
            placeholder="Enter 6-digit code"
            keyboardType="number-pad"
            maxLength={6}
          />

          {isLoading ? (
            <ActivityIndicator size="large" />
          ) : (
            <>
              <TouchableOpacity
                testID="verify-button"
                style={styles.submitButton}
                onPress={handleSubmit}
              >
                <Text style={styles.submitButtonText}>Verify</Text>
              </TouchableOpacity>
              <TouchableOpacity
                testID="resend-button"
                style={styles.resendButton}
                onPress={handleResendCode}
              >
                <Text style={styles.resendButtonText}>Resend Code</Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  formContainer: {
    width: "100%",
    maxWidth: 400,
  },
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    borderRadius: 10,
    padding: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 30,
    textAlign: "center",
    color: "#4A5568",
  },
  label: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 5,
    color: "#4A5568",
  },
  input: {
    backgroundColor: "white",
    borderRadius: 5,
    padding: 10,
    marginBottom: 15,
    fontSize: 16,
  },
  submitButton: {
    backgroundColor: "#4299E1",
    borderRadius: 5,
    padding: 15,
    alignItems: "center",
    marginTop: 10,
  },
  submitButtonText: {
    color: "white",
    fontSize: 18,
    fontWeight: "600",
  },
  resendButton: {
    marginTop: 15,
    alignItems: "center",
  },
  resendButtonText: {
    color: "#4299E1",
    fontSize: 16,
    fontWeight: "600",
  },
});

export default VerifyAccountForm;
